import { BlobGetResponse } from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { isPdfMime } from "~/utils";
import { DeferredDocument, TiffMessageData } from "./interfaces";
import { getPdfThumbnails } from "./pdf";

const typeStorage: Record<string, string> = {};
const thumbnailsStorage: Record<string, string[]> = {};
const renderPromises: Record<string, Promise<ImageData[]>> = {};
const urlCache: Record<string, string> = {};
const objectUrls: string[] = [];

let tiffListeners: [
    string,
    (imageData: ImageData[]) => void,
    (error: string) => void,
][] = [];
let tiffWorker: Worker | null = null;

export const getBlobType = async (url: string) => {
    if (!(url in typeStorage)) {
        try {
            const response = await fetch(url, { method: "HEAD" });

            typeStorage[url] =
                response.headers.get("content-type") ?? "image/png";
        } catch {
            typeStorage[url] = "image/png";
        }
    }

    return typeStorage[url];
};

const getThumbnails = async (type: string, id: string, url: string) => {
    if (!(id in thumbnailsStorage)) {
        if (isPdfMime(type)) {
            thumbnailsStorage[id] = await getPdfThumbnails(url);
        } else {
            // assume it's an image
            thumbnailsStorage[id] = [url];
        }
    }

    return thumbnailsStorage[id];
};

const getTiffWorker = () => {
    if (tiffWorker) {
        return tiffWorker;
    }

    tiffWorker = new Worker("tiff.js");

    tiffWorker.onmessage = (event: MessageEvent<TiffMessageData>) => {
        const { id, pagesData, error } = event.data;
        const newListeners = [];

        for (let i = 0; i < tiffListeners.length; i++) {
            const [url, resolve, reject] = tiffListeners[i];

            if (url === id) {
                if (pagesData) {
                    resolve(pagesData);
                } else {
                    reject(error);
                }
            } else {
                newListeners.push(tiffListeners[i]);
            }
        }

        tiffListeners = newListeners;
    };

    return tiffWorker;
};

const imageDataToUrl = async (imageData: ImageData, id: string) => {
    if (id in urlCache) {
        return urlCache[id];
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Failed to get canvas context");
    }

    canvas.width = imageData.width;
    canvas.height = imageData.height;
    ctx.putImageData(imageData, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
    );

    if (!blob) {
        throw new Error("Failed to convert image data to blob");
    }

    const url = URL.createObjectURL(blob);

    objectUrls.push(url);
    urlCache[id] = url;

    return url;
};

const getRenderPromise = async (document: BlobGetResponse, type: string) => {
    if (type !== "image/tiff") {
        return null;
    }

    if (document.url in renderPromises) {
        return renderPromises[document.url];
    }

    const worker = getTiffWorker();
    const response = await fetch(document.url);

    if (response.status !== 200) {
        return false;
    }

    let arrayBuffer: ArrayBuffer;

    try {
        arrayBuffer = await response.arrayBuffer();
    } catch {
        return false;
    }

    const promise = new Promise<ImageData[]>((resolve, reject) => {
        worker.postMessage({ id: document.url, arrayBuffer });
        tiffListeners.push([document.url, resolve, reject]);
    });

    renderPromises[document.url] = promise;

    return promise;
};

export const deferDocument = (documentPromise: Promise<BlobGetResponse>) => {
    const loading = signal(true);
    const loaded = signal(false);
    const url = signal<string | null>(null);
    const urls = signal<string[]>([]);
    const thumbnails = signal<string[]>([]);
    const error = signal<string | null>(null);
    const type = signal<string | null>(null);
    const blob = signal<BlobGetResponse | null>(null);
    let promise: Promise<ImageData[] | null | false> | null = null;
    let rendered = false;
    let renderRequested = false;

    const process = async () => {
        try {
            const doc = await documentPromise;
            const t = await getBlobType(doc.url);

            promise = getRenderPromise(doc, t);
            blob.value = doc;
            type.value = t;

            if (renderRequested) {
                render();
            }
        } catch (err: unknown) {
            error.value = err instanceof Error ? err.message : "Unknown error";
            loading.value = false;
            loaded.value = true;
        }
    };

    const render = async () => {
        if (rendered) {
            return;
        }

        const b = blob.peek();

        if (!b || !promise) {
            renderRequested = true;
            return;
        }

        rendered = true;

        try {
            const result = await promise;

            if (result === false) {
                error.value = "Failed to render document";
            } else if (result === null) {
                url.value = b.url;
                urls.value = [b.url];
                thumbnails.value = await getThumbnails(
                    type.peek() ?? "",
                    b.url,
                    b.url,
                );
            } else {
                url.value = b.url;
                urls.value = await Promise.all(
                    result.map((imageData, index) =>
                        imageDataToUrl(imageData, `${b.url}-${index}`),
                    ),
                );
                thumbnails.value = urls.peek().slice();
            }
        } catch (err: unknown) {
            error.value = err instanceof Error ? err.message : "Unknown error";
        } finally {
            loading.value = false;
            loaded.value = true;
        }
    };

    process();

    return computed(
        (): DeferredDocument => ({
            blob: blob.value,
            type: type.value,
            loading: loading.value,
            loaded: loaded.value,
            url: url.value,
            urls: urls.value,
            thumbnails: thumbnails.value,
            error: error.value,
            render,
        }),
    );
};

export const stubDocument = () =>
    computed(
        (): DeferredDocument => ({
            blob: null,
            type: null,
            loading: false,
            loaded: true,
            url: null,
            urls: [],
            thumbnails: [],
            error: "No document",
            render: async () => {},
        }),
    );

export const clearObjectUrls = () => {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.length = 0;
};
